import React, { useEffect } from 'react';
import { Grid } from '@mui/material';
import HomeDocumentOverview from 'FeatureModules/Home/Components/HomeDocumentOverview';
import useDialog from 'hooks/useDialog';
import usePageTitle from 'hooks/usePageTitle';
import useConfig from 'hooks/useConfig';
import { EmptyStateCard } from 'DesignSystem';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { Upload } from '@styled-icons/bootstrap/Upload';
import { useQuery } from '@apollo/client';
import { GET_PARTNER_USER_HOME_DATA, GET_PARTNER_REQUEST } from 'graphql/secure/queries';
import { IGetPartnerRequestData } from 'graphql/secure/queries/partner.queries';
import { removeGraphConnections } from 'utils/graphql.utils';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { IGetPartnerUserHomeDataValues } from 'graphql/secure/queries/partner.queries';
import { IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import HomeRequestsOverview from 'FeatureModules/Home/Components/HomeRequestsOverview';
import ReceivedRequestCard from 'FeatureModules/Home/Components/Requests/ReceivedRequestCard';
import Loader from 'Components/Forms/Loader';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';
import ActivitySummary from '../Components/ActivitySummary';
import PageTitle from 'Components/PageTitle';
import PageSubTitle from 'Components/PageSubTitle';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';

const PartnerHome: React.FC = () => {
  usePageTitle('Home');

  const { openDialog } = useDialog();
  const config = useConfig();

  // Handle dialog query parameters
  const [requestReplyOpen, setRequestReplyOpen] = useQueryParam<boolean | undefined | null>(
    config.appQueryParams.requestReplyOpen,
    BooleanParam
  );
  const [chainRequestReplyOpen, setChainRequestReplyOpen] = useQueryParam<
    boolean | undefined | null
  >(config.appQueryParams.chainRequestReplyOpen, BooleanParam);
  const [requestId, setRequestId] = useQueryParam<string | undefined | null>(
    config.appQueryParams.requestId,
    StringParam
  );

  // Query for the specific request when dialog should be opened
  const { data: requestData, loading: loadingRequest } = useQuery<IGetPartnerRequestData>(
    GET_PARTNER_REQUEST,
    {
      variables: { id: requestId },
      skip: !requestId || (!chainRequestReplyOpen && !requestReplyOpen),
    }
  );

  // Handle opening dialogs based on query params
  useEffect(() => {
    if (requestId && requestData?.partnerRequest) {
      if (requestReplyOpen || chainRequestReplyOpen) {
        openDialog({
          type: 'MANAGE_REQUEST',
          props: {
            request: requestData.partnerRequest,
          },
        });
        // Reset query params after opening the dialog
        setRequestReplyOpen(undefined);
        setChainRequestReplyOpen(undefined);
        setRequestId(undefined);
      }
    }
  }, [
    requestId,
    requestData,
    requestReplyOpen,
    chainRequestReplyOpen,
    openDialog,
    setRequestReplyOpen,
    setChainRequestReplyOpen,
    setRequestId,
  ]);

  const openUploadDocumentDialog = () => {
    openDialog({ type: 'UPLOAD_DOCUMENTS' });
  };

  const {
    data,
    loading: loadingDocuments,
    error,
  } = useQuery<IGetPartnerUserHomeDataValues>(GET_PARTNER_USER_HOME_DATA, {
    variables: {
      first: 4,
      filters: {
        requestStatus: ['REQUESTED', 'EDITED'],
      },
      orderBy: { orderByField: 'requestedTimestamp', orderByMethod: 'ASC' },
    },
  });
  const documents: IBaseDocument[] = data ? removeGraphConnections(data.documents) : [];
  const requests: IPartnerRequest[] = data ? removeGraphConnections(data.partnerRequests) : [];
  const hasRequests: boolean = requests.length > 0;
  const showDocuments = !!documents.length;

  const showGettingStarted = !documents.length;

  if (loadingDocuments || loadingRequest) return <Loader />;

  if (error) return <ErrorState />;

  return (
    <PageContainer>
      <PageTitle title="Home" mb={2} />
      <Grid container spacing={3}>
        {hasRequests && (
          <Grid item xs={12}>
            <HomeRequestsOverview
              requests={requests}
              renderRequestCard={request => (
                <ReceivedRequestCard request={request} descriptionHeight={40} key={request.id} />
              )}
              partnerUser={true}
            />
          </Grid>
        )}

        {showGettingStarted && (
          <Grid item xs={6}>
            <PageSubTitle title="Getting Started" mb={1} />
            <EmptyStateCard
              onCreateItemClick={openUploadDocumentDialog}
              variant={EItemTypes.DOCUMENT}
              showSpark={false}
              background={EBackgroundTypes.WORLD}
              buttonText="Upload new"
              size={EAvailableSizes.MEDIUM}
              title="Add documents & certificates to prove your sustainable impact"
              buttonIcon={<Upload size={14} color="white" />}
              data-testid="documents-getting-started-card"
              data-testid-button="create-documents-btn-getting-started"
            />
          </Grid>
        )}
        <ActivitySummary />
        {showDocuments && (
          <Grid item xs={12} lg={6}>
            <HomeDocumentOverview documents={documents} loading={loadingDocuments} />
          </Grid>
        )}
      </Grid>
    </PageContainer>
  );
};

export default PartnerHome;
